import { useEffect, useRef, useState } from 'react';
import whiteLogoUrl from '@sakhtar/design-system/assets/logo/sakhtar-white-logo.svg';
import blackLogoUrl from '@sakhtar/design-system/assets/logo/sakhtar-black-logo.svg';
import './authMobile.css';

const DESKTOP_BREAKPOINT = '(min-width: 900px)';

/** Split hero-left/form-right vs. the design's structurally different
 * stacked mobile layout — not a pure CSS reflow of the same markup, so
 * this switches which JSX tree renders rather than just resizing one. */
export function useIsDesktopAuth(): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_BREAKPOINT).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_BREAKPOINT);
    const onChange = () => setIsDesktop(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
}

/**
 * The pulsing square-grid canvas behind both login screens' desktop hero
 * panel. Ported from the design's own `squaresRef` animation loop
 * (Sakhtar Login.dc.html) — same cell size/gap, same per-cell random
 * phase/speed/peak so the pulse never looks uniform, same "hot cell"
 * (brighter blue) ratio. Pure decoration: no state, no props, cleans up
 * its own rAF loop on unmount.
 *
 * Exported so `StartPage.tsx` can reuse the exact same animation as its
 * own full-page background (reported live: wanted the same treatment
 * there, not just here) — the cell/color constants are shared rather than
 * forked so the two pages stay visually identical without copy-paste.
 */
export function HeroSquaresCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const ctx = el.getContext('2d');
    if (!ctx) return;

    const size = 26;
    const gap = 2;
    const fadeStart = 0;
    const fadeEnd = 0.06;
    const color = '#0062ae';
    const hotColor = '#4aa3ea';
    let cols = 0;
    let rows = 0;
    let cells: { phase: number; speed: number; peak: number; hot: boolean }[] = [];
    let w = 0;
    let h = 0;
    let raf = 0;

    function seed(): boolean {
      const dpr = window.devicePixelRatio || 1;
      w = el!.clientWidth;
      h = el!.clientHeight;
      if (!w || !h) return false;
      el!.width = w * dpr;
      el!.height = h * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(w / (size + gap));
      rows = Math.ceil(h / (size + gap));
      cells = new Array(cols * rows).fill(0).map(() => {
        const hot = Math.random() < 0.12;
        return {
          phase: Math.random() * Math.PI * 2,
          speed: 0.6 + Math.random() * 1.8,
          peak: hot ? 0.85 + Math.random() * 0.15 : 0.18 + Math.random() * 0.45,
          hot,
        };
      });
      return true;
    }

    function draw(t: number) {
      raf = requestAnimationFrame(draw);
      if (!cells.length && !seed()) return;
      if (el!.clientWidth !== w || el!.clientHeight !== h) {
        if (!seed()) return;
      }
      ctx!.clearRect(0, 0, w, h);
      const time = t / 1000;
      for (let r = 0; r < rows; r++) {
        const yN = (r * (size + gap)) / h;
        let fade = 1;
        if (yN < fadeStart) fade = 0;
        else if (yN < fadeEnd) fade = (yN - fadeStart) / (fadeEnd - fadeStart);
        if (fade <= 0) continue;
        for (let c = 0; c < cols; c++) {
          const cell = cells[r * cols + c];
          if (!cell) continue;
          const w0 = Math.sin(time * cell.speed + cell.phase) * 0.5 + 0.5;
          const a = w0 * w0 * cell.peak * fade;
          if (a < 0.015) continue;
          ctx!.globalAlpha = a;
          ctx!.fillStyle = cell.hot ? hotColor : color;
          ctx!.fillRect(c * (size + gap), r * (size + gap), size, size);
        }
      }
      ctx!.globalAlpha = 1;
    }

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
}

/** The two soft ambient-light blobs behind the canvas grid — same reuse
 * reasoning as `HeroSquaresCanvas` above. */
export function HeroGlowBlobs() {
  return (
    <>
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -160,
          insetInlineStart: -120,
          width: 560,
          height: 560,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,.14), rgba(255,255,255,0) 66%)',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: -200,
          insetInlineEnd: -180,
          width: 640,
          height: 640,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(120,170,255,.22), rgba(120,170,255,0) 66%)',
          pointerEvents: 'none',
        }}
      />
    </>
  );
}

/**
 * Desktop-only left hero panel shared by both auth screens
 * (LoginBySlugPage, LoginPage) — identical brand copy on both per the
 * design, so no props needed.
 */
export function AuthHeroPanel() {
  return (
    <div
      style={{
        direction: 'rtl',
        position: 'relative',
        overflow: 'hidden',
        background: 'var(--sk-brand-800, #23277a)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '0 82px',
      }}
    >
      <HeroSquaresCanvas />
      <HeroGlowBlobs />

      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 18,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
          <img src={whiteLogoUrl} alt="" style={{ height: 209, width: 172, display: 'block' }} />
          <span
            style={{
              fontSize: 64,
              fontWeight: 700,
              color: '#ffffff',
              letterSpacing: '-.02em',
              lineHeight: 1.1,
              whiteSpace: 'nowrap',
              textAlign: 'center',
            }}
          >
            ساختار سیستم
          </span>
        </div>
        <span style={{ fontSize: 26, fontWeight: 600, color: '#ffffff', lineHeight: 1.7 }}>
          محیط ابری یکپارچه
        </span>
        <p
          style={{
            margin: 0,
            fontSize: 12,
            lineHeight: 1.9,
            color: 'rgba(255,255,255,.72)',
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          فروش، انبار، مالی و گزارش‌های مدیریتی در یک فضای کاری امن و همیشه در دسترس.
        </p>
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 40,
          insetInline: 0,
          textAlign: 'center',
          fontFamily: 'ui-monospace, monospace',
          fontSize: 11,
          letterSpacing: '.08em',
          color: 'rgba(255,255,255,.42)',
        }}
      >
        SAKHTAR SYSTEM · CLOUD ERP
      </div>
    </div>
  );
}

/** Compact mobile header (logo + wordmark + tagline) — used in place of
 * AuthHeroPanel on narrow viewports, matching the design's mobile frame. */
export function AuthMobileHeader() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <img src={blackLogoUrl} alt="" style={{ height: 34, width: 'auto', display: 'block' }} />
        <span style={{ fontSize: 19, fontWeight: 700, color: 'var(--sk-fg)' }}>ساختار سیستم</span>
      </div>
      <span style={{ fontSize: 13, color: 'var(--sk-fg-muted)' }}>محیط ابری یکپارچه</span>
    </div>
  );
}
